import type { EntityMember, TypeId } from '@sdk';
import { Loader2, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { avatarColorForParticipant } from '@src/components/conversation/avatar-color';
import {
  participantInitials,
  participantLabel,
  participantRoleLabel,
  participantSubtitle,
} from '@src/components/conversation/participant-display';
import { iconForType } from '@src/components/graph-view/icons/iconRegistry';
import { assignableRolesForMember, isGroupMember, memberPrincipalId } from '@src/components/organization/member-roles';

/**
 * The member roster primitives, shared by the account dialog's organization tab
 * and the Organization WorldView drawer.
 *
 * One component, two densities: the dialog renders a read-only summary, the
 * drawer passes ``manage`` to reveal the role control and the remove button. The
 * interaction vocabulary is deliberately the same one the conversation roster
 * already uses (a native ``select`` for the role, an ``X`` to remove), so there is
 * one way to manage members in the product rather than three.
 */

export interface MemberActions {
  /** Change a member's role. Receives the principal id (user or group). */
  onSetRole: (principalId: string, roles: string[]) => Promise<void>;
  /** Remove a member. Receives the principal id (user or group). */
  onRemove: (principalId: string) => Promise<void>;
}

export function MemberRow({
  member,
  isSelf,
  me,
  manage,
  actions,
}: {
  member: EntityMember;
  isSelf: boolean;
  me?: EntityMember | null;
  manage?: boolean;
  actions?: MemberActions;
}) {
  const { t } = useLingui();
  const [busy, setBusy] = useState(false);
  const color = avatarColorForParticipant(member as never, isSelf);
  const label = participantLabel(member as never);
  const subtitle = participantSubtitle(member);
  const initials = participantInitials(member as never);
  const role = participantRoleLabel(member as never);
  const pending = (member.status || '').toLowerCase() === 'pending';
  const isGroup = isGroupMember(member);
  const principalId = memberPrincipalId(member);

  const options = manage && actions ? assignableRolesForMember(me, member) : [];
  const currentRole = member.roles ?? [];
  // A group is rendered with its own glyph rather than initials, so a class
  // inside a school reads as a class and not as a person with odd initials.
  const GroupIcon = isGroup ? iconForType((member as { type?: string }).type || 'team') : null;

  const run = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <li className={`flex items-center gap-2 ${busy ? 'opacity-40' : ''}`} data-testid="member-row">
      {GroupIcon ? (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <GroupIcon size={14} />
        </span>
      ) : (
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${color}`}
        >
          {initials}
        </span>
      )}
      {/* Truncated, so the full label is in a tooltip -- an address is exactly the thing a
          narrow column cuts and a reader needs whole. */}
      <span className="min-w-0 flex-1 truncate text-sm" title={label}>
        {label}
        {isSelf && (
          <span className="text-muted-foreground">
            <Trans> (you)</Trans>
          </span>
        )}
        {subtitle && <span className="ml-2 text-xs text-muted-foreground">{subtitle}</span>}
      </span>
      {manage && actions && options.length > 0 && principalId ? (
        <select
          data-testid="member-role-select"
          aria-label={t`Role`}
          className="rounded-md border border-border bg-background px-1.5 py-0.5 text-xs"
          multiple
          value={currentRole}
          disabled={busy}
          onChange={(e) => void run(() => actions.onSetRole(principalId, Array.from(e.target.selectedOptions, option => option.value)))}
        >
          {options.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      ) : (
        role && <span className="text-xs text-muted-foreground">{role}</span>
      )}
      {pending && (
        <span className="text-xs italic text-muted-foreground">
          <Trans>pending</Trans>
        </span>
      )}
      {manage && actions && principalId && !isSelf && options.length > 0 && (
        <button
          type="button"
          data-testid="member-remove"
          aria-label={t`Remove member`}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          disabled={busy}
          onClick={() => void run(() => actions.onRemove(principalId))}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X size={14} />}
        </button>
      )}
    </li>
  );
}

export function MemberSection({
  title,
  members,
  ready,
  updating,
  stale,
  selfId,
  me,
  manage,
  actions,
  footer,
}: {
  title?: string;
  members: EntityMember[];
  ready: boolean;
  /** Refresh in flight over the shown cache → "updating…". */
  updating?: boolean;
  /** Signed in but the refresh failed → "can't update — showing last synced". */
  stale?: boolean;
  selfId: string;
  me?: EntityMember | null;
  manage?: boolean;
  actions?: MemberActions;
  /** Invite control, rendered by the caller so each surface picks its own. */
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      {title && (
        <div className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
          <span>{title}</span>
          {updating && <Loader2 className="h-3 w-3 animate-spin" />}
        </div>
      )}
      {!ready && members.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          <Trans>Loading members…</Trans>
        </div>
      ) : members.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          <Trans>No members yet.</Trans>
        </div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {members.map((m, i) => (
            <MemberRow
              key={memberPrincipalId(m) ?? m.email ?? i}
              member={m}
              isSelf={m.user_id === selfId}
              me={me}
              manage={manage}
              actions={actions}
            />
          ))}
        </ul>
      )}
      {stale && (
        <div className="text-[11px] text-muted-foreground">
          <Trans>Can't update — showing last synced</Trans>
        </div>
      )}
      {/* Mutations are hidden while offline (stale) — they would only 409. */}
      {!stale && footer}
    </div>
  );
}

export type { EntityMember, TypeId };
